/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const MESSAGE_DATA_PREFIX = '#!CR-ZY-SDK::';
const EVENT_TYPE_SET_RESOURCE_TOKEN = 'set_resource_token';
const EVENT_TYPE_SUBMIT = 'submit';
const EVENT_TYPE_SUBMIT_RESULT = 'submit_result';
const EVENT_TYPE_SUBMIT_INTERMEDIATE_RESULT = 'submit_intermediate_result';
const EVENT_GRADING_DATA_CHANGE = 'grading_data_change';
const EVENT_IDE_OPTIONS_CHANGE = 'ide_options_change';
const EVENT_IDE_VALIDATIONS_CHANGE = 'ide_validations_change';
const EVENT_TYPE_ZY_UPDATE_GRADING_STATE = 'zy_update_grading_state';
const EVENT_TYPE_START_PLAYBACK = 'start_playback';
const EVENT_TYPE_ON_PLAYBACK_START = 'on_playback_start';
const EVENT_TYPE_START_TUTORIAL = 'start_tutorial';
const EVENT_TYPE_OPEN_INSTANCE_TAB = 'open_instance_tab';
const EVENT_TYPE_WORKSPACE_LOAD = 'workspace_load';
const EVENT_IDE_RUN_CODE = 'run_code';
const EVENT_TYPE_FLUSH_USER_DATA = 'flush_user_data';
const EVENT_TYPE_FLUSH_USER_DATA_RESULT = 'flush_user_data_result';
const EVENT_TYPE_SEND_SUBMISSION_EXPLORE_RUN_DATA = 'send_submission_explore_run_data';
const EVENT_DARK_MODE_TOGGLE = 'dark_mode_toggle';
const EVENT_TYPE_USER_ACTIVE = 'user_active';
const SINGLE_FILE_CODE_TEST_TYPE_ZYLAB_MANUAL_DEDUCTION = 'zylab-manual-deduction';
const EVENT_TYPE_SAVE_OPTIONS = 'save_options';
const EVENT_TYPE_SAVE_OPTIONS_TESTCASES = 'save_options_test_cases';
const EVENT_TYPE_SAVE_OPTIONS_TESTCASES_RESULT = 'save_options_test_cases_result';
const EVENT_TYPE_INSTRUCTIONS_GENERATED = 'instructions_generated';
const EVENT_TYPE_REQUEST_INSTRUCTIONS = 'request_instructions';
const EVENT_TYPE_REQUEST_INSTRUCTIONS_RESPONSE = 'request_instructions_response';
const EVENT_TYPE_UPDATE_STEPS = 'update_steps';
const EVENT_TYPE_AUTH_READY = 'auth_ready';
const EVENT_TYPE_AUTH_INIT = 'auth_init';

class Logger {
    constructor(appName) {
        this.appName = appName;
    }
    log(...args) {
        console.log(this.prefix(), ...args);
    }
    error(...args) {
        console.error(this.prefix(), ...args);
    }
    debug(...args) {
        console.debug(this.prefix(), ...args);
    }
    warn(...args) {
        console.warn(this.prefix(), ...args);
    }
    prefix() {
        return `[${this.appName}] `;
    }
}
const logger = new Logger('CR-SDK');

const randStrCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const randStrCharactersLength = randStrCharacters.length;
function isIFrame(input) {
    return !!(input && input.tagName === 'IFRAME');
}
function randomString(length) {
    let result = '';
    for (let i = 0; i < length; i++) {
        result += randStrCharacters.charAt(Math.floor(Math.random() * randStrCharactersLength));
    }
    return result;
}
function bindEvent(element, eventName, eventHandler) {
    if (element.addEventListener) {
        element.addEventListener(eventName, eventHandler, false);
    }
    else if (element.attachEvent) {
        element.attachEvent('on' + eventName, eventHandler);
    }
}
function unbindEvent(element, eventName, eventHandler) {
    if (element.removeEventListener) {
        element.removeEventListener(eventName, eventHandler, false);
    }
    else if (element.detachEvent) {
        element.detachEvent('on' + eventName, eventHandler);
    }
}
function timeoutAfter(seconds, error = false) {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            if (error) {
                reject(new Error('request timed-out'));
            }
            else {
                resolve();
            }
        }, seconds * 1000);
    });
}

const DEFAULT_URL = 'https://zystudio-dev.zybooks.com';
class CodingRooms {
    constructor(parentElement, options) {
        this.parentElement = parentElement;
        this.options = options;
        this.zombie = false;
        this.handlers = {};
        this.frameId = `cr-zy-${randomString(8)}`;
        this.onMsgFunc = this.onMessage.bind(this);
        bindEvent(window, 'message', this.onMsgFunc);
    }
    createIFrame(domain) {
        this.iFrameTarget = document.createElement('iframe');
        this.iFrameTarget.src = domain;
        this.iFrameTarget.width = '100%';
        this.iFrameTarget.height = '100%';
        this.iFrameTarget.setAttribute('allow', 'accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking; serial; clipboard-read; clipboard-write');
        this.iFrameTarget.setAttribute('sandbox', 'allow-forms allow-downloads allow-modals allow-popups allow-presentation allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation');
        this.iFrameTarget.setAttribute('allowFullScreen', 'true');
        this.iFrameTarget.setAttribute('title', 'Code and output');
        // iFrameTarget.frameBorder ="0";
        // iFrameTarget.scrolling = "0";
        this.iFrameTarget.style.border = 'none';
        this.iFrameTarget.style.background = 'white';
        this.parentElement.appendChild(this.iFrameTarget);
    }
    dispose() {
        if (this.zombie) {
            return;
        }
        this.zombie = true;
        this.handlers = {};
        unbindEvent(window, 'message', this.onMsgFunc);
    }
    subscribe(event, handler) {
        const hId = randomString(16);
        if (!event || typeof event !== 'string') {
            throw new Error('Invalid/missing event key on subscribe call: ' + event);
        }
        if (!handler || typeof handler !== 'function') {
            throw new Error('Invalid/missing handler function on subscribe call: ' + handler);
        }
        if (!this.handlers[event] || typeof this.handlers[event] !== 'object') {
            this.handlers[event] = {};
        }
        this.handlers[event][hId] = handler;
        return hId;
    }
    unsubscribe(event, hId) {
        if (!event || typeof event !== 'string') {
            throw new Error('Invalid/missing event key on unsubscribe call: ' + event);
        }
        if (!hId || typeof hId !== 'string') {
            throw new Error('Invalid/missing handler id on unsubscribe call: ' + event);
        }
        if (!this.handlers[event] || typeof this.handlers[event] !== 'object' || !this.handlers[event][hId]) {
            logger.warn(`Already unsubscribed event=${event} handlerId=${hId}`);
            return;
        }
        delete this.handlers[event][hId];
    }
    init(resourceToken, zyUserId, metadata) {
        var _a, _b, _c, _d, _e;
        this.resourceToken = resourceToken;
        this.zyUserId = zyUserId;
        this.aciToken = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.aciToken) !== null && _a !== void 0 ? _a : '';
        this.zyUserRole = (_b = metadata === null || metadata === void 0 ? void 0 : metadata.zyUserRole) !== null && _b !== void 0 ? _b : '';
        this.hasCompletedProblemDecomposition = (_c = metadata === null || metadata === void 0 ? void 0 : metadata.hasCompletedProblemDecomposition) !== null && _c !== void 0 ? _c : false;
        this.problemDecompositionSteps = (_d = metadata === null || metadata === void 0 ? void 0 : metadata.problemDecompositionSteps) !== null && _d !== void 0 ? _d : [];
        this.canRunCheckProgram = (_e = metadata === null || metadata === void 0 ? void 0 : metadata.canRunCheckProgram) !== null && _e !== void 0 ? _e : false;
        let url = new URL('/zystudio/auth/callback', this.options.url || DEFAULT_URL).toString();
        url = `${url}#frameId=${this.frameId}`;
        if (this.options.debug) {
            url += '&debug=true';
        }
        if (typeof this.options.autosave === 'boolean') {
            url += `&autosave=${this.options.autosave}`;
        }
        if (this.options.isDarkMode) {
            url += `&isDarkMode=${this.options.isDarkMode}`;
        }
        if (this.options.forceEnablePlayback) {
            url += `&forceEnablePlayback=${this.options.forceEnablePlayback}`;
        }
        if (this.options.hideSubmissionLimits) {
            url += `&hideSubmissionLimits=${this.options.hideSubmissionLimits}`;
        }
        if (this.options.accessibilityMode) {
            url += `&accessibilityMode=${this.options.accessibilityMode}`;
        }
        if (this.options.playbackTimestamp) {
            url += `&timestamp=${this.options.playbackTimestamp}`;
        }
        if (this.options.instanceParams) {
            url += `&subpath=${this.options.instanceParams.subpath}&workspaceId=${this.options.instanceParams.workspaceId}}`;
        }
        if (this.options.isNewTab) {
            url += '&isNewTab=true';
        }
        if (this.options.testBenchOptions) {
            url += `&testBenchOptions=${encodeURIComponent(JSON.stringify(this.options.testBenchOptions))}`;
        }
        if (this.options.environment) {
            url += `&environment=${this.options.environment}`;
        }
        const authReadyHandlerId = this.subscribe(EVENT_TYPE_AUTH_READY, () => {
            this.sendMessage(EVENT_TYPE_AUTH_INIT, {
                resourceToken: this.resourceToken,
                aciToken: this.aciToken,
                zyUserId: this.zyUserId,
                zyUserRole: this.zyUserRole,
                hasCompletedProblemDecomposition: this.hasCompletedProblemDecomposition,
                problemDecompositionSteps: this.problemDecompositionSteps,
                canRunCheckProgram: this.canRunCheckProgram,
            });
            this.unsubscribe(EVENT_TYPE_AUTH_READY, authReadyHandlerId);
        });
        if (this.options.onGradingDataChanges) {
            this.onGradingDataChanges(this.options.onGradingDataChanges);
        }
        if (this.options.onIDEOptionsChanges) {
            this.onIDEOptionsChanges(this.options.onIDEOptionsChanges);
        }
        if (this.options.onIDEValidationsChanges) {
            this.onIDEValidationsChanges(this.options.onIDEValidationsChanges);
        }
        if (this.options.onIntermediateSubmitResultChanges) {
            this.onIntermediateSubmitResultChanges(this.options.onIntermediateSubmitResultChanges);
        }
        if (this.options.onOpenInstanceInNewTab) {
            this.onOpenInstanceInNewTab(this.options.onOpenInstanceInNewTab);
        }
        if (this.options.onWorkspaceLoad) {
            this.onWorkspaceLoad(this.options.onWorkspaceLoad);
        }
        if (this.options.onRunCode) {
            this.onRunCode(this.options.onRunCode);
        }
        if (this.options.onPlaybackStart) {
            this.onPlaybackStart(this.options.onPlaybackStart);
        }
        if (this.options.onUserActive) {
            this.onUserActive(this.options.onUserActive);
        }
        if (this.options.onInstructionsGenerated) {
            this.onInstructionsGenerated(this.options.onInstructionsGenerated);
        }
        if (this.options.onRequestInstructions) {
            this.onRequestInstructions(this.options.onRequestInstructions);
        }
        this.createIFrame(url);
    }
    setWorkspace(resourceToken) {
        this.sendMessage(EVENT_TYPE_SET_RESOURCE_TOKEN, {
            zyUserId: this.zyUserId,
            resourceToken,
        });
    }
    sendSubmissionsAndExploreRuns(payload) {
        this.sendMessage(EVENT_TYPE_SEND_SUBMISSION_EXPLORE_RUN_DATA, payload);
    }
    sendInstructions(payload) {
        this.sendMessage(EVENT_TYPE_REQUEST_INSTRUCTIONS_RESPONSE, payload);
    }
    sendSteps(payload) {
        this.sendMessage(EVENT_TYPE_UPDATE_STEPS, payload);
    }
    toggleDarkMode() {
        this.sendMessage(EVENT_DARK_MODE_TOGGLE, {});
    }
    zyUpdateGradingState(hasTestCases, maxScore) {
        this.sendMessage(EVENT_TYPE_ZY_UPDATE_GRADING_STATE, {
            hasTestCases,
            maxScore,
        });
    }
    startPlayback(timestamp) {
        this.sendMessage(EVENT_TYPE_START_PLAYBACK, { timestamp });
    }
    startTutorial() {
        this.sendMessage(EVENT_TYPE_START_TUTORIAL, {});
    }
    saveOptionsTestCases() {
        const responseId = randomString(16);
        this.sendMessage(EVENT_TYPE_SAVE_OPTIONS_TESTCASES, { responseId });
        return new Promise((resolve, reject) => {
            try {
                const handlerId = this.subscribe(EVENT_TYPE_SAVE_OPTIONS_TESTCASES_RESULT, (event, payload) => {
                    if (payload.responseId === responseId) {
                        resolve(payload.result);
                        this.unsubscribe(EVENT_TYPE_SAVE_OPTIONS_TESTCASES_RESULT, handlerId);
                    }
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    saveOptions(options, force = false) {
        this.sendMessage(EVENT_TYPE_SAVE_OPTIONS, { options, force });
    }
    submit() {
        const responseId = randomString(16);
        this.sendMessage(EVENT_TYPE_SUBMIT, { responseId });
        return new Promise((resolve, reject) => {
            try {
                const handlerId = this.subscribe(EVENT_TYPE_SUBMIT_RESULT, (event, payload) => {
                    if (payload.responseId === responseId) {
                        resolve(payload.result);
                        this.unsubscribe(EVENT_TYPE_SUBMIT_RESULT, handlerId);
                    }
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    flushUserData() {
        const responseId = randomString(16);
        this.sendMessage(EVENT_TYPE_FLUSH_USER_DATA, { responseId });
        const flushUserDataPromise = new Promise((resolve, reject) => {
            try {
                const handlerId = this.subscribe(EVENT_TYPE_FLUSH_USER_DATA_RESULT, (_event, payload) => {
                    if (payload.responseId === responseId) {
                        resolve(payload.result);
                        this.unsubscribe(EVENT_TYPE_FLUSH_USER_DATA_RESULT, handlerId);
                    }
                });
            }
            catch (error) {
                reject(error);
            }
        });
        return Promise.race([flushUserDataPromise, timeoutAfter(3)]);
    }
    onRunCode(callback) {
        if (this.runCodeHandlerId) {
            this.unsubscribe(EVENT_IDE_RUN_CODE, this.runCodeHandlerId);
        }
        this.runCodeHandlerId = this.subscribe(EVENT_IDE_RUN_CODE, (_event, _payload) => {
            callback(_payload.enableCheckProgramOnRun);
        });
    }
    onUserActive(callback) {
        if (this.userActiveHandlerId) {
            this.unsubscribe(EVENT_TYPE_USER_ACTIVE, this.userActiveHandlerId);
        }
        this.userActiveHandlerId = this.subscribe(EVENT_TYPE_USER_ACTIVE, (_event, _payload) => {
            callback();
        });
    }
    onPlaybackStart(callback) {
        if (this.playbackStartHandlerId) {
            this.unsubscribe(EVENT_TYPE_ON_PLAYBACK_START, this.playbackStartHandlerId);
        }
        this.playbackStartHandlerId = this.subscribe(EVENT_TYPE_ON_PLAYBACK_START, (_event, _payload) => {
            callback();
        });
    }
    onInstructionsGenerated(callback) {
        if (this.instructionsGeneratedHandlerId) {
            this.unsubscribe(EVENT_TYPE_INSTRUCTIONS_GENERATED, this.instructionsGeneratedHandlerId);
        }
        this.instructionsGeneratedHandlerId = this.subscribe(EVENT_TYPE_INSTRUCTIONS_GENERATED, (_event, payload) => {
            callback(payload);
        });
    }
    onRequestInstructions(callback) {
        if (this.requestInstructionsHandlerId) {
            this.unsubscribe(EVENT_TYPE_REQUEST_INSTRUCTIONS, this.requestInstructionsHandlerId);
        }
        this.requestInstructionsHandlerId = this.subscribe(EVENT_TYPE_REQUEST_INSTRUCTIONS, (_event, payload) => {
            callback(payload);
        });
    }
    onGradingDataChanges(callback) {
        if (this.testCaseChangeHandlerId) {
            this.unsubscribe(EVENT_GRADING_DATA_CHANGE, this.testCaseChangeHandlerId);
        }
        this.testCaseChangeHandlerId = this.subscribe(EVENT_GRADING_DATA_CHANGE, (event, payload) => {
            callback(payload, Math.round(((payload || []).reduce(
            // round to nearest hundredths
            (totalPoints, testCase) => {
                if (testCase.type === SINGLE_FILE_CODE_TEST_TYPE_ZYLAB_MANUAL_DEDUCTION) {
                    return totalPoints;
                }
                return totalPoints + (testCase.points || 0);
            }, 0) +
                Number.EPSILON) *
                100) / 100);
        });
    }
    onIDEOptionsChanges(callback) {
        if (this.IDEOptionsChangeHandlerId) {
            this.unsubscribe(EVENT_IDE_OPTIONS_CHANGE, this.IDEOptionsChangeHandlerId);
        }
        this.IDEOptionsChangeHandlerId = this.subscribe(EVENT_IDE_OPTIONS_CHANGE, (_event, payload) => callback(payload));
    }
    onIDEValidationsChanges(callback) {
        if (this.IDEValidationsChangeHandlerId) {
            this.unsubscribe(EVENT_IDE_VALIDATIONS_CHANGE, this.IDEValidationsChangeHandlerId);
        }
        this.IDEValidationsChangeHandlerId = this.subscribe(EVENT_IDE_VALIDATIONS_CHANGE, (_event, payload) => callback(payload));
    }
    onIntermediateSubmitResultChanges(callback) {
        if (this.intermediateSubmitResultChangeHandlerId) {
            this.unsubscribe(EVENT_TYPE_SUBMIT_INTERMEDIATE_RESULT, this.intermediateSubmitResultChangeHandlerId);
        }
        this.intermediateSubmitResultChangeHandlerId = this.subscribe(EVENT_TYPE_SUBMIT_INTERMEDIATE_RESULT, (_event, payload) => callback(payload));
    }
    onOpenInstanceInNewTab(callback) {
        if (this.openInstanceHandlerId) {
            this.unsubscribe(EVENT_TYPE_OPEN_INSTANCE_TAB, this.openInstanceHandlerId);
        }
        this.openInstanceHandlerId = this.subscribe(EVENT_TYPE_OPEN_INSTANCE_TAB, (_event, payload) => callback(payload));
    }
    onWorkspaceLoad(callback) {
        if (this.workspaceLoadHandlerId) {
            this.unsubscribe(EVENT_TYPE_WORKSPACE_LOAD, this.workspaceLoadHandlerId);
        }
        this.workspaceLoadHandlerId = this.subscribe(EVENT_TYPE_WORKSPACE_LOAD, (_event, payload) => callback(payload));
    }
    /**
     * Should be avoided since event names and payload shapes can change - use exposed API
     */
    sendMessage(event, payload) {
        this.assertNotZombie();
        if (isIFrame(this.iFrameTarget) && this.iFrameTarget.contentWindow) {
            this.iFrameTarget.contentWindow.postMessage(MESSAGE_DATA_PREFIX +
                JSON.stringify({
                    frameId: this.frameId,
                    event,
                    payload,
                }), '*');
        }
        else {
            console.error('[CRSDK] Failed to send message, not a valid iframe');
        }
    }
    assertNotZombie() {
        if (this.zombie) {
            logger.error(`Function called on zombie instance; frameId=${this.frameId}`);
            throw new Error('Cannot call this function on a zombie instance');
        }
    }
    onMessage(event) {
        if (this.zombie) {
            logger.warn(`Zombie instance still receiving messages. Dispose likely failed. frameId=${this.frameId}`);
            return;
        }
        if (!event || !event.data) {
            return;
        }
        if (typeof event.data !== 'string' || !event.data.startsWith(MESSAGE_DATA_PREFIX)) {
            // Catering for browser console messages
            if (isIFrame(this.iFrameTarget) && this.iFrameTarget.contentWindow && event.data.method) {
                this.iFrameTarget.contentWindow.postMessage({
                    method: event.data.method,
                    args: event.data.args,
                    origin: event.origin,
                }, '*');
            }
            return;
        }
        let msg;
        try {
            msg = JSON.parse(event.data.substr(MESSAGE_DATA_PREFIX.length));
            if (!msg.frameId || typeof msg.frameId !== 'string') {
                logger.error('message.frameId is missing/invalid');
                return;
            }
            if (msg.frameId !== this.frameId) {
                logger.debug("message.frameId doesn't match, skipping");
                return;
            }
            if (!msg.event || typeof msg.event !== 'string') {
                logger.error('message.event is missing/invalid');
                return;
            }
        }
        catch (err) {
            logger.error('Exception parsing message data: ', err);
            return;
        }
        this.notifySubscribers(msg.event, msg.payload);
    }
    notifySubscribers(event, payload) {
        if (!this.handlers[event] || typeof this.handlers[event] !== 'object') {
            return;
        }
        const handlerIds = Object.keys(this.handlers[event]);
        if (!handlerIds || handlerIds.length < 1) {
            return;
        }
        handlerIds.forEach(id => {
            const func = this.handlers[event][id];
            if (!func || typeof func !== 'function') {
                return;
            }
            try {
                let a = () => __awaiter(this, void 0, void 0, function* () {
                    func(event, payload);
                });
                // Ignore promise -- don't wait for this to terminate, just call the other handlers
                a();
            }
            catch (err) {
                logger.error(`Error calling handler frameId=${this.frameId} event=${event} handlerId=${id} `, err);
            }
        });
    }
}

export { CodingRooms };
